import * as React from 'react';
import {
    styled,
    Switch as MuiSwitch,
    type SwitchProps as MuiSwitchProps,
    PaletteColor,
} from '@mui/material';
import './Switch.css';
import { FaOptionIcon, checkIcon, errorIcon } from '@europrocurement/l2d-icons';
import { lighten, Theme } from '@mui/system';

export type NativeSwitchProps = MuiSwitchProps;

export type SwitchProps = Omit<NativeSwitchProps, 'value'> & {
    value?: boolean;
    defaultValue?: boolean;
    color?: PaletteColor;
};

export type SwitchStyleProps = {
    theme: Theme;
    color: PaletteColor;
};

/**
 * Switch component :
 * Checked value priority : Value > Default value > false
 */
export const Switch: React.FunctionComponent<SwitchProps> = styled(({ ...props }: SwitchProps) => {
    const { defaultValue = false, value, onChange, ...otherProps } = props;

    const isControlled = typeof value !== 'undefined';

    const switchProps = isControlled
        ? {
              checked: !!value,
              onChange,
          }
        : {
              defaultChecked: !!defaultValue,
              onChange,
          };

    const checkedIcon = <FaOptionIcon {...checkIcon.props} />;
    const icon = <FaOptionIcon {...errorIcon.props} />;

    return (
        <MuiSwitch
            {...otherProps}
            {...switchProps}
            role="switch"
            icon={icon}
            checkedIcon={checkedIcon}
        />
    );
})(({ theme, color = theme.palette.primary.main }: SwitchStyleProps) => {
    const textColorGreyDark = theme.palette.grey[500];
    const backgroundColorGrey = theme.palette.grey[200];
    const backgroundDisabledColorGrey = theme.palette.grey[100];
    const backgroundColorPrimaryLight = lighten(color as unknown as string, 0.82);
    const toggleColorWhite = theme.palette.background.paper;
    const toggleColorPrimary = color;

    return {
        '& .MuiSwitch-switchBase': {
            backgroundColor: backgroundColorGrey,
            '&:before': {
                backgroundColor: toggleColorWhite,
            },
            '&:hover': {
                backgroundColor: backgroundColorGrey,
            },
            '.MuiTypography-root': {
                color: textColorGreyDark,
            },
            '&.Mui-checked': {
                backgroundColor: backgroundColorPrimaryLight,
                '&:before': {
                    backgroundColor: toggleColorPrimary,
                },
                '&:hover': {
                    backgroundColor: backgroundColorPrimaryLight,
                },
                '.MuiTypography-root': {
                    color: toggleColorPrimary,
                },
            },
            '&.Mui-disabled': {
                backgroundColor: backgroundDisabledColorGrey,
                '&:before': {
                    backgroundColor: backgroundDisabledColorGrey,
                },
                '&.Mui-checked': {
                    backgroundColor: backgroundColorPrimaryLight,
                    opacity: 0.5,
                    '&:before': {
                        backgroundColor: toggleColorPrimary,
                    },
                },
            },
        },
    };
});
